import React, { useRef, useState } from "react";
import { toast } from "sonner";
import { Upload, X, FileText, Image as ImageIcon, File as FileIcon, Loader2 } from "lucide-react";
import { Button } from "./ui/button";

const ALLOWED_MIMES = [
  "application/pdf", "image/jpeg", "image/png", "image/webp",
  "application/msword",
  "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
];
const ALLOWED_EXTS = ["pdf", "jpg", "jpeg", "png", "webp", "doc", "docx"];
const MAX_BYTES = 10 * 1024 * 1024;
const MAX_DEFAULT = 10;

const fmtSize = (n) => {
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(0)} KB`;
  return `${(n / 1024 / 1024).toFixed(1)} MB`;
};

const iconFor = (mime, name) => {
  const lower = (name || "").toLowerCase();
  if ((mime || "").startsWith("image/") || /\.(jpg|jpeg|png|webp)$/.test(lower))
    return <ImageIcon size={14} className="text-violet-500" />;
  if ((mime || "").includes("word") || /\.(doc|docx)$/.test(lower))
    return <FileText size={14} className="text-sky-600" />;
  if ((mime || "") === "application/pdf" || lower.endsWith(".pdf"))
    return <FileText size={14} className="text-red-500" />;
  return <FileIcon size={14} className="text-slate-400" />;
};

/**
 * MultiDocumentUploader — anexa até N documentos (default 10).
 *
 * Modo "local" (default): mantém arquivos em memória até o submit do form.
 *   - Pai recebe array de File via onChange.
 *   - Útil para upload em lote (FormData) na mesma requisição.
 *
 * Modo "remoto" (uploadUrl): envia cada arquivo imediatamente via POST multipart
 *   para uploadUrl (campo `documentos`). Pai recebe array de metadados salvos.
 *
 * Props:
 *   value      — array atual ({ file?, id?, nome_original/filename, mime?, size? })
 *   onChange   — callback(arr)
 *   maxFiles   — limite (default 10)
 *   label      — título acima
 *   sublabel   — auxiliar (default: "Opcional · PDF, DOC, JPG, PNG, WEBP · 10 MB")
 *   disabled   — desabilita input
 */
export default function MultiDocumentUploader({
  value = [],
  onChange,
  maxFiles = MAX_DEFAULT,
  label = "Documentos",
  sublabel = "Opcional · PDF, DOC, JPG, PNG, WEBP · 10 MB por arquivo",
  disabled = false,
}) {
  const fileRef = useRef(null);
  const [uploading, setUploading] = useState(false);

  const validar = (file) => {
    if (file.size > MAX_BYTES) {
      toast.error(`${file.name}: muito grande (${fmtSize(file.size)}). Limite 10 MB.`);
      return false;
    }
    const ext = file.name.split(".").pop()?.toLowerCase() || "";
    if (!ALLOWED_MIMES.includes(file.type) && !ALLOWED_EXTS.includes(ext)) {
      toast.error(`${file.name}: tipo não suportado.`);
      return false;
    }
    return true;
  };

  const onPick = () => fileRef.current?.click();

  const onFiles = async (e) => {
    const list = Array.from(e.target.files || []);
    e.target.value = "";
    if (!list.length) return;
    const livres = maxFiles - value.length;
    if (list.length > livres) {
      toast.error(`Restam apenas ${livres} de ${maxFiles} vagas`);
      return;
    }
    const validos = list.filter(validar);
    if (!validos.length) return;
    setUploading(true);
    try {
      const novos = validos.map((file) => ({
        file,
        local: true,
        nome_original: file.name,
        mime: file.type,
        size: file.size,
      }));
      onChange?.([...value, ...novos]);
    } finally {
      setUploading(false);
    }
  };

  const remover = (idx) => {
    onChange?.(value.filter((_, i) => i !== idx));
  };

  const slots = Math.max(0, maxFiles - value.length);

  return (
    <div className="space-y-2" data-testid="multi-doc-uploader">
      {label && (
        <div className="flex items-center justify-between">
          <span className="text-sm font-medium text-slate-700">{label}</span>
          <span className="text-[10px] text-slate-400">
            {value.length}/{maxFiles}
          </span>
        </div>
      )}
      <p className="text-[11px] text-slate-500">{sublabel}</p>

      {value.length > 0 && (
        <ul className="space-y-1.5 max-h-44 overflow-y-auto" data-testid="multi-doc-list">
          {value.map((doc, idx) => (
            <li
              key={doc.id || idx}
              className="flex items-center gap-2 px-2.5 py-1.5 rounded-md border border-slate-200 bg-slate-50/50 text-xs"
              data-testid={`doc-row-${idx}`}
            >
              {iconFor(doc.mime, doc.nome_original || doc.filename)}
              <span className="truncate flex-1 text-slate-700">
                {doc.nome_original || doc.filename}
              </span>
              {doc.size != null && (
                <span className="text-[10px] text-slate-400 shrink-0">{fmtSize(doc.size)}</span>
              )}
              {!disabled && (
                <button
                  type="button"
                  onClick={() => remover(idx)}
                  className="text-slate-400 hover:text-red-500 shrink-0"
                  data-testid={`doc-remove-${idx}`}
                >
                  <X size={13} />
                </button>
              )}
            </li>
          ))}
        </ul>
      )}

      {!disabled && slots > 0 && (
        <Button
          type="button"
          variant="outline"
          size="sm"
          onClick={onPick}
          disabled={uploading}
          className="text-xs"
          data-testid="multi-doc-add-btn"
        >
          {uploading ? (
            <Loader2 size={13} className="mr-1.5 animate-spin" />
          ) : (
            <Upload size={13} className="mr-1.5" />
          )}
          Adicionar documento ({slots} {slots === 1 ? "vaga" : "vagas"})
        </Button>
      )}

      <input
        ref={fileRef}
        type="file"
        multiple
        accept=".pdf,.jpg,.jpeg,.png,.webp,.doc,.docx,application/pdf,image/jpeg,image/png,image/webp,application/msword,application/vnd.openxmlformats-officedocument.wordprocessingml.document"
        onChange={onFiles}
        className="hidden"
      />
    </div>
  );
}
